import React, { useState } from "react";
import { Container, Form, Row, Col } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "./UnifiedFormStyles.css";
import AppButton from "../../Components/MerchantButton";

const PREFERRED_TERMINALS = [
  "Metrobank Terminal",
  "BDO Terminal",
  "Gcash SQR/Terminal",
  "Other",
];

const CHOOSE_PREFERRED_REASONS = [
  "User-friendly",
  "Fast transactions",
  "Reliable",
  "No competing terminal available",
  "Better customer support",
  "Better settlement time",
  "Better transaction success rate",
  "Lower fees",
  "Required by company/head office",
  "Customers prefer Maya",
  "Other",
];

const NOT_ACCEPT_MAYA_REASONS = [
  "Head Office / Management policy",
  "Transaction issues",
  "High fees",
  "Lack of information about Maya",
  "Security concerns",
  "Other",
];

function NonMayaMerchantsSurvey({ navigation, primaryDetails, setPrimaryDetails }) {
  const [isError, setIsError] = useState({});

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setPrimaryDetails((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "non_maya_preferred_terminal" && value !== "Other"
        ? { non_maya_preferred_terminal_other: "" }
        : {}),
    }));
    if (isError[name]) {
      setIsError((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    setPrimaryDetails((prev) => {
      const currentValues = prev[name] || [];
      const nextValues = checked
        ? [...currentValues, value]
        : currentValues.filter((item) => item !== value);

      const updates = { [name]: nextValues };

      if (!checked && value === "Other") {
        if (name === "non_maya_choose_preferred_reasons") {
          updates.non_maya_choose_preferred_other = "";
        }
        if (name === "non_maya_not_accept_maya_reasons") {
          updates.non_maya_not_accept_maya_other = "";
        }
      }

      return { ...prev, ...updates };
    });

    if (isError[name]) {
      setIsError((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setPrimaryDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (isError[name]) {
      setIsError((prev) => ({ ...prev, [name]: false }));
    }
  };

  const hasOtherText = (selected, optionLabel, textValue) => {
    if (!(selected || []).includes(optionLabel)) return true;
    return !!(textValue && textValue.trim());
  };

  const validateForm = () => {
    const errors = {};

    if (!primaryDetails.non_maya_preferred_terminal) {
      errors.non_maya_preferred_terminal = true;
    }
    if (
      primaryDetails.non_maya_preferred_terminal === "Other" &&
      !(primaryDetails.non_maya_preferred_terminal_other || "").trim()
    ) {
      errors.non_maya_preferred_terminal_other = true;
    }

    const choosePreferred = primaryDetails.non_maya_choose_preferred_reasons || [];
    if (choosePreferred.length === 0) {
      errors.non_maya_choose_preferred_reasons = true;
    }
    if (
      !hasOtherText(
        choosePreferred,
        "Other",
        primaryDetails.non_maya_choose_preferred_other
      )
    ) {
      errors.non_maya_choose_preferred_other = true;
    }

    const notAccept = primaryDetails.non_maya_not_accept_maya_reasons || [];
    if (notAccept.length === 0) {
      errors.non_maya_not_accept_maya_reasons = true;
    }
    if (
      !hasOtherText(notAccept, "Other", primaryDetails.non_maya_not_accept_maya_other)
    ) {
      errors.non_maya_not_accept_maya_other = true;
    }

    setIsError(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please complete all required fields.", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return false;
    }
    return true;
  };

  const isFormValid = () => {
    if (!primaryDetails.non_maya_preferred_terminal) return false;
    if (
      primaryDetails.non_maya_preferred_terminal === "Other" &&
      !(primaryDetails.non_maya_preferred_terminal_other || "").trim()
    ) {
      return false;
    }

    const choosePreferred = primaryDetails.non_maya_choose_preferred_reasons || [];
    if (choosePreferred.length === 0) return false;
    if (
      !hasOtherText(
        choosePreferred,
        "Other",
        primaryDetails.non_maya_choose_preferred_other
      )
    ) {
      return false;
    }

    const notAccept = primaryDetails.non_maya_not_accept_maya_reasons || [];
    if (notAccept.length === 0) return false;
    return hasOtherText(
      notAccept,
      "Other",
      primaryDetails.non_maya_not_accept_maya_other
    );
  };

  const handleNext = () => {
    if (validateForm()) {
      navigation.go("visibility-photo");
    }
  };

  const handleBack = () => {
    navigation.previous();
  };

  const renderCheckboxGroup = (name, options, errorKey) => (
    <div className="form-checkbox-group">
      {options.map((option) => {
        const selected = (primaryDetails[name] || []).includes(option);
        return (
          <div
            key={`${name}-${option}`}
            className={`form-checkbox-item${selected ? " selected" : ""}`}
          >
            <div className="form-checkbox-flex">
              <Form.Check
                type="checkbox"
                name={name}
                value={option}
                label={option}
                checked={selected}
                onChange={handleCheckboxChange}
                className="form-checkbox"
              />
            </div>
          </div>
        );
      })}
      {isError[errorKey] && (
        <div className="form-error">Please select at least one option</div>
      )}
    </div>
  );

  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">
          <Col md={10}>
            <div className="form-main-header">
              <h4 className="form-title">NON MAYA MERCHANTS SURVEY</h4>
              <p className="form-desc">
                Please answer based on the non-Maya merchant&apos;s preferences
              </p>
            </div>

            <div className="form-main">
              <div className="form-main-content">
                <Form className="form-form">
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      What is the merchant&apos;s preferred payment terminal?
                      <span className="form-required">*</span>
                    </Form.Label>
                    <div className="form-radio-group">
                      {PREFERRED_TERMINALS.map((option) => (
                        <div
                          key={option}
                          className={`form-radio-item${
                            primaryDetails.non_maya_preferred_terminal === option
                              ? " selected"
                              : ""
                          }`}
                        >
                          <div className="form-radio-flex">
                            <Form.Check
                              type="radio"
                              name="non_maya_preferred_terminal"
                              value={option}
                              label={option}
                              checked={
                                primaryDetails.non_maya_preferred_terminal === option
                              }
                              onChange={handleRadioChange}
                              className="form-radio"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    {isError.non_maya_preferred_terminal && (
                      <div className="form-error">Please select an option</div>
                    )}
                    {primaryDetails.non_maya_preferred_terminal === "Other" && (
                      <Form.Control
                        type="text"
                        name="non_maya_preferred_terminal_other"
                        value={primaryDetails.non_maya_preferred_terminal_other || ""}
                        onChange={handleTextChange}
                        placeholder="Please specify"
                        className={`form-input mt-2${
                          isError.non_maya_preferred_terminal_other
                            ? " form-input-error"
                            : ""
                        }`}
                      />
                    )}
                    {isError.non_maya_preferred_terminal_other && (
                      <div className="form-error">Please specify Other</div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Why does the merchant choose their preferred terminal?
                      <span className="form-required">*</span>
                    </Form.Label>
                    {renderCheckboxGroup(
                      "non_maya_choose_preferred_reasons",
                      CHOOSE_PREFERRED_REASONS,
                      "non_maya_choose_preferred_reasons"
                    )}
                    {(primaryDetails.non_maya_choose_preferred_reasons || []).includes(
                      "Other"
                    ) && (
                      <Form.Control
                        type="text"
                        name="non_maya_choose_preferred_other"
                        value={primaryDetails.non_maya_choose_preferred_other || ""}
                        onChange={handleTextChange}
                        placeholder="Please specify"
                        className={`form-input mt-2${
                          isError.non_maya_choose_preferred_other
                            ? " form-input-error"
                            : ""
                        }`}
                      />
                    )}
                    {isError.non_maya_choose_preferred_other && (
                      <div className="form-error">Please specify Other</div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Why doesn&apos;t the merchant accept Maya as a payment method?
                      <span className="form-required">*</span>
                    </Form.Label>
                    {renderCheckboxGroup(
                      "non_maya_not_accept_maya_reasons",
                      NOT_ACCEPT_MAYA_REASONS,
                      "non_maya_not_accept_maya_reasons"
                    )}
                    {(primaryDetails.non_maya_not_accept_maya_reasons || []).includes(
                      "Other"
                    ) && (
                      <Form.Control
                        type="text"
                        name="non_maya_not_accept_maya_other"
                        value={primaryDetails.non_maya_not_accept_maya_other || ""}
                        onChange={handleTextChange}
                        placeholder="Please specify"
                        className={`form-input mt-2${
                          isError.non_maya_not_accept_maya_other
                            ? " form-input-error"
                            : ""
                        }`}
                      />
                    )}
                    {isError.non_maya_not_accept_maya_other && (
                      <div className="form-error">Please specify Other</div>
                    )}
                  </Form.Group>

                  <div className="form-btn-row">
                    <AppButton
                      onClick={handleBack}
                      variant="back"
                      className="form-btn variant-secondary"
                    >
                      ← Back
                    </AppButton>
                    <AppButton
                      onClick={handleNext}
                      variant="next"
                      className="form-btn variant-primary"
                      disabled={!isFormValid()}
                    >
                      Next →
                    </AppButton>
                  </div>
                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default NonMayaMerchantsSurvey;
